import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
  notification,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";

import {
  ApiError,
  type CorrectionReasonRow,
  type TransactionList,
  type UserRow,
  api,
} from "../api/client";
import { UserQuickSwitch } from "../components/UserQuickSwitch";
import { UserTransactionsList } from "../components/UserTransactionsList";
import { VolunteerDaysSection } from "../components/VolunteerDaysSection";
import { CorrectionsBatchReviewModal } from "../components/CorrectionsBatchReviewModal";
import { usePendingBatch } from "../hooks/usePendingBatch";
import { formatCents } from "../utils/dateFormat";

const { Title, Text } = Typography;

export const UserDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const strichlisteUserId = Number(id);

  const [user, setUser] = useState<UserRow | null>(null);
  const [transactions, setTransactions] = useState<TransactionList | null>(null);
  const [reasons, setReasons] = useState<CorrectionReasonRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);

  const pending = usePendingBatch();

  const refresh = useCallback(async () => {
    if (!Number.isFinite(strichlisteUserId)) {
      setError("invalid user id");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [u, t, r] = await Promise.all([
        api.getUserByStrichliste(strichlisteUserId),
        api.listUserTransactions(strichlisteUserId),
        api.listCorrectionReasons(true),
      ]);
      setUser(u);
      setTransactions(t);
      setReasons(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [strichlisteUserId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onPatch = async (patch: Record<string, unknown>) => {
    if (!user) return;
    try {
      const next = await api.updateUser(user.strichliste_user_id, patch);
      setUser(next);
      notification.success({ message: "Saved", duration: 1 });
    } catch (err) {
      notification.error({
        message: "Save failed",
        description: err instanceof ApiError ? err.message : String(err),
      });
    }
  };

  const onToggleDisabled = async () => {
    if (!user) return;
    if (user.disabled) {
      try {
        const next = await api.reenableUser(user.strichliste_user_id);
        setUser(next);
      } catch (err) {
        notification.error({
          message: "Re-enable failed",
          description: err instanceof ApiError ? err.message : String(err),
        });
      }
      return;
    }
    if (user.balance_cents !== null && user.balance_cents !== 0) {
      notification.warning({
        message: "Settle the balance first",
        description: `Current balance is ${formatCents(user.balance_cents)}. Use the Settle Balance button.`,
      });
      return;
    }
    Modal.confirm({
      title: "Disable user?",
      content: `Disable ${user.name}? Their balance is zero and they'll be hidden from the kiosk.`,
      onOk: async () => {
        try {
          const next = await api.disableUser(user.strichliste_user_id);
          setUser(next);
        } catch (err) {
          notification.error({
            message: "Disable failed",
            description: err instanceof ApiError ? err.message : String(err),
          });
        }
      },
    });
  };

  if (loading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }
  if (error || !user || !transactions) {
    return (
      <Card>
        <Alert
          type="error"
          message={error || "User not found"}
          showIcon
          action={
            <Button onClick={() => navigate("/users")}>Back to list</Button>
          }
        />
      </Card>
    );
  }

  const balanceNonZero = user.balance_cents !== null && user.balance_cents !== 0;

  return (
    <Card>
      {/* Three-row header: back-link, title, quick-switch search.
          Mirrors the user-list page's "title + search-bar-below"
          layout so jumping between views feels consistent. */}
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/users")}
        style={{ paddingLeft: 0 }}
      >
        Back to list
      </Button>
      <Title
        level={2}
        style={{ marginTop: 4, marginBottom: 12, wordBreak: "break-word" }}
      >
        {user.name}
        {user.disabled && <Tag style={{ marginLeft: 12 }}>Disabled</Tag>}
      </Title>
      <div style={{ marginBottom: 4 }}>
        <UserQuickSwitch currentUserId={user.strichliste_user_id} />
      </div>

      <Descriptions
        // Responsive: stack to one column at xs/sm so each field gets
        // breathing room on mobile; two columns from md up. Safe to use
        // a responsive `column` here now that the notes have moved to
        // their own block below — no `span` clamping issues to manage.
        column={{ xs: 1, sm: 1, md: 2 }}
        bordered
        size="small"
        style={{ marginTop: 16 }}
      >
        <Descriptions.Item label="Nickname">
          <Input
            size="small"
            defaultValue={user.name}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next && next !== user.name) {
                void onPatch({ name: next });
              }
            }}
            placeholder="—"
          />
        </Descriptions.Item>
        <Descriptions.Item label="Real name">
          <Input
            size="small"
            defaultValue={user.real_name ?? ""}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next !== (user.real_name ?? "")) {
                void onPatch({ real_name: next || null });
              }
            }}
            placeholder="—"
          />
        </Descriptions.Item>
        <Descriptions.Item label="Email">
          <Input
            size="small"
            defaultValue={user.email ?? ""}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next !== (user.email ?? "")) {
                // Empty string -> backend stores NULL via the same path.
                void onPatch({ email: next || null });
              }
            }}
            placeholder="—"
          />
        </Descriptions.Item>
        <Descriptions.Item label="Snipe-IT email">
          <Input
            size="small"
            defaultValue={user.erp_email ?? ""}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next !== (user.erp_email ?? "")) {
                // ERP-only override: used when this person's Snipe-IT email
                // differs from their strichliste/CSV email so badge sync
                // still resolves them. Empty string -> backend stores NULL.
                void onPatch({ erp_email: next || null });
              }
            }}
            placeholder="—"
          />
        </Descriptions.Item>
        <Descriptions.Item label="Type">
          {user.user_type === "bank" ? (
            <Tag color="purple">bank</Tag>
          ) : (
            <Select<"regular" | "guest">
              size="small"
              style={{ width: 140 }}
              value={user.user_type}
              onChange={(next) => {
                if (next !== user.user_type) void onPatch({ user_type: next });
              }}
              options={[
                { value: "regular", label: "Regular" },
                { value: "guest", label: "Guest" },
              ]}
            />
          )}
        </Descriptions.Item>
      </Descriptions>

      {/* Notes in their own labelled block below Descriptions. Single
          column (label above value) so they always have the full card
          width and never compete with the 2-col form grid above. */}
      <div className="user-notes-block">
        <NoteField
          label="Public note"
          value={user.public_note}
          placeholder="Visible on the kiosk under the user's balance"
          onSave={(next) => onPatch({ public_note: next })}
        />
        <NoteField
          label="Internal note"
          value={user.internal_note}
          placeholder="Admin only"
          onSave={(next) => onPatch({ internal_note: next })}
        />
      </div>

      {/* Balance + Settle + active switch as a unified action bar.
          Plain text + matched-height Buttons + Switch read as one bar
          instead of three competing visual styles. */}
      <div
        style={{
          marginTop: 16,
          padding: "10px 16px",
          background: "var(--ant-color-fill-quaternary)",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
          <Text type="secondary">Balance</Text>
          <Text
            strong
            style={{
              fontSize: 18,
              color: balanceNonZero ? "#ff4d4f" : "#52c41a",
            }}
          >
            {formatCents(user.balance_cents)}
          </Text>
        </span>
        <Space wrap>
          <Button
            type="primary"
            onClick={() =>
              navigate(`/settlements?user_id=${user.strichliste_user_id}`)
            }
          >
            Settle balance
          </Button>
          <Tooltip
            title={
              balanceNonZero && !user.disabled
                ? `Settle the balance first (current: ${formatCents(user.balance_cents)})`
                : ""
            }
          >
            <span>
              <Switch
                checked={!user.disabled}
                disabled={balanceNonZero && !user.disabled}
                onChange={onToggleDisabled}
                checkedChildren="Active"
                unCheckedChildren="Disabled"
              />
            </span>
          </Tooltip>
        </Space>
      </div>

      <VolunteerDaysSection
        strichlisteUserId={strichlisteUserId}
        onChanged={() => void refresh()}
      />

      <div
        style={{
          marginTop: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Transaction history ({transactions.total})
        </Title>
        <Space>
          <Button
            danger
            disabled={pending.size === 0}
            onClick={() => pending.clear()}
          >
            Clear batch
          </Button>
          <Button
            type="primary"
            disabled={pending.size === 0}
            onClick={() => setBatchOpen(true)}
          >
            {pending.size === 0
              ? "Commit corrections"
              : `Commit ${pending.size} correction${pending.size === 1 ? "" : "s"}`}
          </Button>
        </Space>
      </div>
      <UserTransactionsList
        transactions={transactions.items}
        reasons={reasons}
        pending={pending}
      />
      <CorrectionsBatchReviewModal
        open={batchOpen}
        onClose={() => setBatchOpen(false)}
        onComplete={() => {
          setBatchOpen(false);
          void refresh();
        }}
        pending={pending}
        reasons={reasons}
        transactions={transactions.items}
        currentBalance={user.balance_cents}
      />
    </Card>
  );
};

/**
 * One labelled-textarea row that visually matches the bordered
 * Descriptions above. Save-on-blur, full-width regardless of viewport.
 */
function NoteField({
  label,
  value,
  placeholder,
  onSave,
}: {
  label: string;
  value: string | null;
  placeholder?: string;
  onSave: (next: string) => void | Promise<void>;
}) {
  return (
    <div className="user-note-row">
      <div className="user-note-label">{label}</div>
      <div className="user-note-value">
        <Input.TextArea
          autoSize={{ minRows: 2, maxRows: 6 }}
          defaultValue={value ?? ""}
          onBlur={(e) => {
            const next = e.target.value;
            if (next !== (value ?? "")) void onSave(next);
          }}
          placeholder={placeholder}
          variant="borderless"
        />
      </div>
    </div>
  );
}
